import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

export interface SearchableSelectOption {
  value: string
  label: string
}

export interface SearchableSelectProps {
  label?: string
  value: string
  onChange: (value: string) => void
  options: SearchableSelectOption[]
  placeholder?: string
  searchPlaceholder?: string
  loading?: boolean
  emptyText?: string
  disabled?: boolean
}

interface DropdownPosition {
  top: number
  left: number
  width: number
  maxHeight: number
}

function computePosition(trigger: HTMLElement): DropdownPosition {
  const rect = trigger.getBoundingClientRect()
  const viewportHeight = window.innerHeight
  // Prefer opening below the trigger; if there isn't enough room, flip above.
  const spaceBelow = viewportHeight - rect.bottom - 8
  const spaceAbove = rect.top - 8
  const naturalHeight = Math.min(240, Math.max(spaceBelow, spaceAbove))
  const openAbove = spaceBelow < 160 && spaceAbove > spaceBelow
  return {
    top: openAbove ? rect.top - naturalHeight - 4 : rect.bottom + 4,
    left: rect.left,
    width: rect.width,
    maxHeight: naturalHeight,
  }
}

/** Combobox-style picker: type to filter, click or use arrow keys + Enter to select.
 *  The dropdown is portaled to document.body so modal overflow rules cannot clip it. */
export function SearchableSelect({
  label,
  value,
  onChange,
  options,
  placeholder = '— select —',
  searchPlaceholder = 'Search…',
  loading = false,
  emptyText = 'No matches',
  disabled = false,
}: SearchableSelectProps) {
  const autoId = useId()
  const inputId = `${autoId}-search`
  const listId = `${autoId}-list`

  const selected = options.find((o) => o.value === value)
  const [open, setOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [highlighted, setHighlighted] = useState(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const [position, setPosition] = useState<DropdownPosition | null>(null)

  const filtered = options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))

  useEffect(() => {
    if (open) {
      setQuery('')
      setHighlighted(0)
    }
  }, [open])

  useEffect(() => {
    setHighlighted(0)
  }, [query])

  useLayoutEffect(() => {
    if (!open || !triggerRef.current) return
    function reposition() {
      if (triggerRef.current) setPosition(computePosition(triggerRef.current))
    }
    reposition()
    window.addEventListener('resize', reposition)
    window.addEventListener('scroll', reposition, true)
    return () => {
      window.removeEventListener('resize', reposition)
      window.removeEventListener('scroll', reposition, true)
    }
  }, [open])

  useEffect(() => {
    function onClickOutside(e: MouseEvent) {
      const target = e.target as Node
      if (
        containerRef.current &&
        !containerRef.current.contains(target) &&
        !(target instanceof Element && target.closest(`[data-searchable-select="${autoId}"]`))
      ) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', onClickOutside)
    return () => document.removeEventListener('mousedown', onClickOutside)
  }, [autoId])

  function select(option: SearchableSelectOption) {
    onChange(option.value)
    setOpen(false)
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (!open) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        e.preventDefault()
        setOpen(true)
      }
      return
    }
    if (e.key === 'Escape') {
      e.preventDefault()
      setOpen(false)
      return
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setHighlighted((i) => Math.min(i + 1, filtered.length - 1))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setHighlighted((i) => Math.max(i - 1, 0))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      const option = filtered[highlighted]
      if (option) select(option)
    }
  }

  const dropdown =
    open && position
      ? createPortal(
          <div
            data-searchable-select={autoId}
            className="fixed z-[100] rounded-sm border border-ostrel-line bg-ostrel-card shadow-lg"
            style={{
              top: position.top,
              left: position.left,
              width: position.width,
              maxHeight: position.maxHeight,
            }}
          >
            <input
              id={inputId}
              autoFocus
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={searchPlaceholder}
              className="w-full rounded-t-sm border-b border-ostrel-line px-3 py-2 text-sm text-ostrel-ink placeholder:text-ostrel-ink-muted focus:outline-none"
              aria-autocomplete="list"
              aria-controls={listId}
            />
            <ul
              id={listId}
              role="listbox"
              className="overflow-auto py-1"
              style={{ maxHeight: Math.max(position.maxHeight - 44, 80) }}
              aria-label={label ?? 'Options'}
            >
              {loading ? (
                <li className="px-3 py-2 text-xs text-ostrel-ink-muted">Loading…</li>
              ) : filtered.length === 0 ? (
                <li className="px-3 py-2 text-xs text-ostrel-ink-muted">{emptyText}</li>
              ) : (
                filtered.map((option, idx) => (
                  <li
                    key={option.value}
                    role="option"
                    aria-selected={option.value === value}
                    onMouseEnter={() => setHighlighted(idx)}
                    onClick={() => select(option)}
                    className={`cursor-pointer px-3 py-2 text-sm ${
                      idx === highlighted
                        ? 'bg-ostrel-sand text-ostrel-ink'
                        : 'text-ostrel-ink hover:bg-ostrel-sand'
                    }`}
                  >
                    {option.label}
                  </li>
                ))
              )}
            </ul>
          </div>,
          document.body,
        )
      : null

  return (
    <div ref={containerRef} className="flex flex-col gap-1.5">
      {label && (
        <label htmlFor={inputId} className="text-[10px] font-semibold uppercase tracking-widest text-ostrel-ink-muted">
          {label}
        </label>
      )}
      <button
        type="button"
        ref={triggerRef}
        id={autoId}
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-haspopup="listbox"
        disabled={disabled}
        onClick={() => !disabled && setOpen((v) => !v)}
        className={`w-full rounded-sm border border-ostrel-line bg-ostrel-card px-3 py-2 text-left text-sm focus:outline-none focus:ring-1 focus:ring-ostrel-gold disabled:opacity-60 ${
          open ? 'ring-1 ring-ostrel-gold' : ''
        }`}
      >
        <span className={selected ? 'text-ostrel-ink' : 'text-ostrel-ink-muted'}>
          {selected?.label ?? placeholder}
        </span>
      </button>
      {dropdown}
    </div>
  )
}
